/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import {useDispatch, useSelector} from "react-redux"; // Importing Redux hooks to interact with state
import {useTranslation} from "react-i18next"; // Importing the translation hook for localization
import {useEffect, useState} from "react"; // Importing React hooks for managing side effects and state
import {RemoveCardToUser} from "../../../reduxToolkit/Cards/CardsApi"; // Action to remove card assignment to a user
import {DeleteItemsApi} from "../../../reduxToolkit/Commons/CommonsApi"; // Action for deleting items from the API
import {clearSearchFilters} from "../../../reduxToolkit/Search/SearchSlice"; // Action to clear search filters
import {MODELS} from "../../../Apis/Models"; // Importing model configurations
import {TABLES} from "../../../Apis/Tables"; // Importing table configurations
import { toast } from "react-toastify";
import { errorMsg } from "../../../Apis/functions";
import { useNavigate } from "react-router-dom";

// The CardsPanelApp component
const CardsPanelApp = () => {
    // Redux dispatch function
    const dispatch = useDispatch();
    const { t } = useTranslation(); // Translation function for localizing strings

    // Local state variables for managing various UI states and logic
    const [isAllChecked, setIsAllChecked] = useState(false); // Whether all items are selected
    const [creationErrorsShow, setCreationErrorsShow] = useState(false); // Show errors for card creation
    const [addCardShow, setAddCardShow] = useState(false); // Show the card creation modal
    const [assignCardShow, setAssignCardShow] = useState(false); // Show assignment modal
    const [disassignCardShow, setDisassignCardShow] = useState(false); // Show assignment modal
    const [cardToAssign, setCardToAssign] = useState(); // Stores card information to assign
    const [currentId, setCurrentId] = useState(''); // Stores the current card ID for preview
    const [unassignModalShow, setUnassignModalShow] = useState(false); // Show unassign modal
    const [cardToUnassign, setCardToUnassign] = useState([]); // Stores card information to unassign
    const [deleteCardShow, setDeleteCardShow] = useState(false); // Show delete confirmation modal
    const [selectedCards, setSelectedCards] = useState([]); // Stores selected card IDs
    const [previewShow, setPreviewShow] = useState(false); // Show preview modal
    const [search, setSearch] = useState(false); // Search flag
    const [bodyForExport, setBodyForExport] = useState(''); // Data to export
    const [searchElement, setSearchElement] = useState(''); // Search filter value
    const [simpleSearch, setSimpleSearch] = useState(false); // Simple search flag
    const [modified, setModified] = useState(false); // Flag to track modifications
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const navigate = useNavigate();

    // Accessing various states from Redux store
    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Get development mode state
    const { permission } = useSelector((state) => state.authenticatioauthennSlice); // Get user permissions

    // Defining model and table constants
    const moduleId = `${MODELS.Card}`;
    const option = `${TABLES.CARD}`;

    // useEffect to clean up search filters when component unmounts
    useEffect(() => {
        return () => {
            dispatch(clearSearchFilters()); // Dispatch action to clear search filters on component unmount
        };
    }, [dispatch]);

    // Function to unassign a card from a user
    const unassignCard = (deleteItem) => {
        const body = {
            cardIds: [deleteItem[0].cardId],
            userId: deleteItem[0].userId,
        };

        dispatch(RemoveCardToUser(body)).then(
            ({
                 payload: {
                     data: { success, code },
                 },
             }) => {
                if (success === true) {
                    setUnassignModalShow(false); // Hide unassign modal
                    setModified(true); // Mark the data as modified
                     toast.success(t('has_been_disassigning_successfully'));
                }else{
                    errorMsg(code)
                }
            },
        );
    };

    // Function to delete selected cards
    const deleteSelectedCard = (deleteItem) => {
        const body = deleteItem; // Body of the delete request
        const tableName = TABLES.CARD; // Table name to delete from

        if (body.length > 0) {
            dispatch(DeleteItemsApi({ tableName, body })).then(
                ({
                     payload: {
                         data: { success },
                     },
                 }) => {
                    if (success === true) {
                        setIsAllChecked(false); // Uncheck the "select all" checkbox after successful deletion
                        setSelectedCards([]);
                        setModified(true);
                    }
                },
            );
        }
    };

    // Function to handle preview of a card
    const handlePreview = (idCard) => {
        setPreviewShow(true); // Show the preview modal
        setCurrentId(idCard); // Set the current card ID for preview
    };

    // Function to handle assignment or unassignment of a card
    const onAssignUnassign = (item) => {

        if (item.userId !== null) {
            setCardToUnassign([
                { cardId: item?.id, userId: item?.userId }, // Prepare data for unassignment
            ]);
            setUnassignModalShow(true); // Show unassign modal
        } else {
            setAssignCardShow(true); // Show assignment modal
            setCardToAssign({
                id: item?.id,
                number: item?.number,
                facilityCode: item?.facilityCode,
                format: item?.format,
            }); // Prepare card data for assignment
        }
    };

    const onDisassignTemplate = (item) => {
        setCurrentId(item?.id);
       setDisassignCardShow(true);
    }

    // Return the component's state and functions for use in the UI
    return {
        t,
        DEV,
        permission,
        modified,
        setModified,
        search,
        setSearch,
        moduleId,
        option,
        bodyForExport,
        setBodyForExport,
        searchElement,
        setSearchElement,
        creationErrorsShow,
        setCreationErrorsShow,
        addCardShow,
        setAddCardShow,
        selectedCards,
        setSelectedCards,
        deleteCardShow,
        setDeleteCardShow,
        isAllChecked,
        setIsAllChecked,
        simpleSearch,
        setSimpleSearch,
        unassignModalShow,
        setUnassignModalShow,
        previewShow,
        setPreviewShow,
        assignCardShow,
        setAssignCardShow,
        disassignCardShow,
        setDisassignCardShow,
        cardToUnassign,
        cardToAssign,
        currentId,
        results,
        setResults,
        unassignCard,
        deleteSelectedCard,
        onAssignUnassign,
        handlePreview,
        onDisassignTemplate,
        navigate
    }
}

export default CardsPanelApp;
